import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, {
  getReferrals,
  getReferralStats,
  generateReferralLink,
} from '../../services/api';

// NOTE: the client expects additional endpoints like `/referrals/activity` and
// `/referrals/analytics` for more detailed reporting. If these endpoints are
// not yet implemented on the server, implement them there or adjust the
// endpoints used below.

// Async thunks
export const fetchReferrals = createAsyncThunk(
  'referrals/fetchReferrals',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getReferrals();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchReferralStats = createAsyncThunk(
  'referrals/fetchReferralStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getReferralStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const generateNewReferralLink = createAsyncThunk(
  'referrals/generateNewReferralLink',
  async (_, { rejectWithValue }) => {
    try {
      const response = await generateReferralLink();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Additional thunks for activity and analytics
export const fetchReferralActivity = createAsyncThunk(
  'referrals/fetchReferralActivity',
  async ({ page = 1, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get(`/referrals/activity?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchReferralAnalytics = createAsyncThunk(
  'referrals/fetchReferralAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/referrals/analytics');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  referrals: [],
  stats: {
    totalPoints: 0,
    referralCount: 0,
    statsByStatus: {}
  },
  referralLink: '',
  loading: {
    referrals: false,
    stats: false,
    link: false,
    activity: false,
    analytics: false
  },
  error: {
    referrals: null,
    stats: null,
    link: null,
    activity: null,
    analytics: null
  }
};

// Add default shapes for activity and analytics in initialState
initialState.activity = { data: [], page: 1, limit: 50, total: 0 };
initialState.analytics = { weeklySeries: [], monthlySeries: [], totalReferrals: 0, totalPoints: 0 };

const referralsSlice = createSlice({
  name: 'referrals',
  initialState,
  reducers: {
    clearReferralErrors: (state) => {
      state.error = {
        referrals: null,
        stats: null,
        link: null
      };
    }
  },
  extraReducers: (builder) => {
    // Fetch referrals
    builder.addCase(fetchReferrals.pending, (state) => {
      state.loading.referrals = true;
      state.error.referrals = null;
    });
    builder.addCase(fetchReferrals.fulfilled, (state, action) => {
      state.loading.referrals = false;
      state.referrals = action.payload;
    });
    builder.addCase(fetchReferrals.rejected, (state, action) => {
      state.loading.referrals = false;
      state.error.referrals = action.payload?.error || 'Failed to fetch referrals';
    });

    // Fetch referral stats
    builder.addCase(fetchReferralStats.pending, (state) => {
      state.loading.stats = true;
      state.error.stats = null;
    });
    builder.addCase(fetchReferralStats.fulfilled, (state, action) => {
      state.loading.stats = false;
      state.stats = action.payload;
    });
    builder.addCase(fetchReferralStats.rejected, (state, action) => {
      state.loading.stats = false;
      state.error.stats = action.payload?.error || 'Failed to fetch referral stats';
    });

    // Generate referral link
    builder.addCase(generateNewReferralLink.pending, (state) => {
      state.loading.link = true;
      state.error.link = null;
    });
    builder.addCase(generateNewReferralLink.fulfilled, (state, action) => {
      state.loading.link = false;
      state.referralLink = action.payload.referralLink;
    });
    builder.addCase(generateNewReferralLink.rejected, (state, action) => {
      state.loading.link = false;
      state.error.link = action.payload?.error || 'Failed to generate referral link';
    });

    // Fetch referral activity
    builder.addCase(fetchReferralActivity.pending, (state) => {
      state.loading.activity = true;
      state.error.activity = null;
    });
    builder.addCase(fetchReferralActivity.fulfilled, (state, action) => {
      state.loading.activity = false;
      // expected shape: [{id, points, reason, referredUserId, createdAt, referredUser}, ...]
      state.activity = action.payload;
    });
    builder.addCase(fetchReferralActivity.rejected, (state, action) => {
      state.loading.activity = false;
      state.error.activity = action.payload?.error || 'Failed to fetch referral activity';
    });

    // Fetch referral analytics
    builder.addCase(fetchReferralAnalytics.pending, (state) => {
      state.loading.analytics = true;
      state.error.analytics = null;
    });
    builder.addCase(fetchReferralAnalytics.fulfilled, (state, action) => {
      state.loading.analytics = false;
      state.analytics = action.payload;
    });
    builder.addCase(fetchReferralAnalytics.rejected, (state, action) => {
      state.loading.analytics = false;
      state.error.analytics = action.payload?.error || 'Failed to fetch referral analytics';
    });
  }
});

export const { clearReferralErrors } = referralsSlice.actions;

// Selectors
export const selectReferrals = (state) => state.referrals.referrals;
export const selectReferralStats = (state) => state.referrals.stats;
export const selectReferralLink = (state) => state.referrals.referralLink;
export const selectReferralLoading = (state) => state.referrals.loading;
export const selectReferralError = (state) => state.referrals.error;
export const selectReferralActivity = (state) => state.referrals.activity;
export const selectReferralAnalytics = (state) => state.referrals.analytics;

export default referralsSlice.reducer;